import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Videocam, Image, Edit, Close, History } from "@mui/icons-material";
import {
  Grid, Box, TextField, Button, IconButton, Typography, Paper, Modal,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText
} from "@mui/material";
import "./Style.css";

import Header from "./MenuBar";
import Footer from "./Footer";
const envVariable = require("../EnvironmentVariables.json");

import { NotificationContext } from "../redux/NotificationProvider";


const Chat = () => {
  const [customerMessages, setCustomerMessages] = useState([]);
  const [mechanicMessages, setMechanicMessages] = useState([]);
  const [newCustomerMessage, setNewCustomerMessage] = useState("");
  const [newMechanicMessage, setNewMechanicMessage] = useState("");
  const [/* uploading */, setUploading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const location = useLocation();
  const { taskId } = useParams();
  const SC_Id = Number(localStorage.getItem("SC_Id"));
  const User_Id = Number(localStorage.getItem("User_Id"));

  // Retrieve customer and mechanic information from location state
  const customerName = location.state?.customerName ?? "Customer";
  const mechanicName = location.state?.mechanicName ?? "Mechanic";
  const customerId = Number(location.state?.customerId ?? null);
  const mechanicId = Number(location.state?.mechanicId ?? null);

  const customerEndRef = useRef(null);
  const mechanicEndRef = useRef(null);

  // Derive WebSocket URL from serverId (same logic as wsClient.js)
  const getWsUrl = () => {
    if (process.env.REACT_APP_WS_URL) {
      return process.env.REACT_APP_WS_URL;
    }
    try {
      const serverUrl = new URL(envVariable.serverId);
      const protocol = serverUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${serverUrl.hostname}:8080/`;
    } catch (e) {
      console.error('[SCO-CHAT] Error parsing serverId:', e);
      return 'ws://localhost:8080/';
    }
  };

  const WS_URL = getWsUrl();
  const WS_JWT_URL = process.env.REACT_APP_WS_JWT_URL;
  // state/refs
  const wsRef = React.useRef(null);
  const connectingRef = React.useRef(false);
  const [wsReady, setWsReady] = React.useState(false);
  const backoffRef = React.useRef(500);

  // fetch token (web uses cookies or your X-API-KEY header, same as RN)
  // BEFORE:
// async function fetchWsToken() {
//   const api_key = localStorage.getItem('api_key');
//   const res = await fetch(WS_JWT_URL, { headers: { 'X-API-KEY': api_key || '' }});
//   const j = await res.json();
//   if (!res.ok || !j?.token) throw new Error('token fetch failed');
//   return j.token;
// }

  async function fetchWsToken() {
    const api_key =
      localStorage.getItem('@api_key') ||
      localStorage.getItem('api_key') ||
      '';
    if (!api_key) throw new Error('no api key in storage');

    const url = WS_JWT_URL || (envVariable.serverId.replace(/\/$/, '') + '/Auth/ws_token.php');

    const res = await fetch(url, { method: 'GET', headers: { 'X-API-KEY': api_key } });
    const txt = await res.text();
    if (!res.ok) throw new Error(`token http ${res.status} ${txt}`);
    let json;
    try { json = JSON.parse(txt); } catch { throw new Error('non-JSON from ws_token'); }
    if (!json.token) throw new Error('no token');
    return json.token;
  }


  function subscribe(ws, taskId) {
   if (!ws) return;
   const payload = { type: 'subscribe', channel: `chat:${Number(taskId)}` };
   if (ws.readyState === WebSocket.OPEN) {
     console.log('[SCO-CHAT] WS → subscribe', payload);
     ws.send(JSON.stringify(payload));
   } else {
     console.log('[SCO-CHAT] subscribe deferred; state=', ws.readyState);
     // queue a one-shot to run when it actually opens
     const onOpen = () => {
       try {
         ws.send(JSON.stringify(payload));
       } catch (e) {
         console.debug('[SCO-CHAT] WS send failed during onOpen:', e.message);
       }
       ws.removeEventListener('open', onOpen);
     };
     ws.addEventListener('open', onOpen);
   }
 }

  React.useEffect(() => {
    let closed = false;
    (async () => {
      try {
        if (wsRef.current || connectingRef.current) return;
        connectingRef.current = true;
        const token = await fetchWsToken();
        console.log('[SCO-CHAT] WS connecting', WS_URL);

        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        connectingRef.current = false;
        const myWs = ws;

        ws.onopen = () => {
          console.log('[SCO-CHAT] WS open → auth');
          ws.send(JSON.stringify({ type: 'auth', token }));
        };

        ws.onmessage = (ev) => {
          if (wsRef.current !== myWs) return;
          let msg; try { msg = JSON.parse(ev.data); } catch { return; }
          console.log('[SCO-CHAT] WS ⇐', msg);

          if (msg.type === 'ack' && msg.of === 'auth') {
            subscribe(ws, taskId);
            return;
          }

          if (msg.type === 'ack' && msg.of === 'subscribe') {
            setWsReady(true);
            backoffRef.current = 500;
            // optional: stop polling if you poll
            return;
          }

          if (msg.type === 'message:created' && Number(msg.task_id) === Number(taskId)) {
            const mm = {
              ...msg.message,
              recipient_id: msg.message.recepient_id,
              mediaType: msg.message.message_type,
            };
            // append to your messages list (same shape as RN did)
            // setCustomerMessages / setMechanicMessages, or a single list setMessages(...)
            if (mm.sender_id === customerId || mm.recipient_id === customerId) {
              setCustomerMessages(prev => prev.some(x => x.chat_id === mm.chat_id) ? prev : [...prev, mm]);
            }
            if (mm.sender_id === mechanicId || mm.recipient_id === mechanicId) {
              setMechanicMessages(prev => prev.some(x => x.chat_id === mm.chat_id) ? prev : [...prev, mm]);
            }
          }

          if (msg.type === 'message:updated' && Number(msg.task_id) === Number(taskId)) {
            console.log('[SCO-CHAT] message:updated received', { chat_id: msg.chat_id, new_message: msg.new_message });
            // Update the specific message in both customer and mechanic lists
            setCustomerMessages(prev =>
              prev.map(m => m.chat_id === msg.chat_id
                ? { ...m, message: msg.new_message, edited: 1 }
                : m)
            );
            setMechanicMessages(prev =>
              prev.map(m => m.chat_id === msg.chat_id
                ? { ...m, message: msg.new_message, edited: 1 }
                : m)
            );
          }

          if (msg.type === 'message:deleted' && Number(msg.task_id) === Number(taskId)) {
            console.log('[SCO-CHAT] message:deleted received', { chat_id: msg.chat_id });
            // Mark the specific message as deleted in both lists
            setCustomerMessages(prev =>
              prev.map(m => m.chat_id === msg.chat_id
                ? { ...m, message: '__DELETED__' }
                : m)
            );
            setMechanicMessages(prev =>
              prev.map(m => m.chat_id === msg.chat_id
                ? { ...m, message: '__DELETED__' }
                : m)
            );
          }

          if (msg.type === 'resync' && Number(msg.task_id) === Number(taskId)) {
            console.log('[SCO-CHAT] resync → HTTP fetch');
            fetchChatMessages();
          }
        };

        ws.onerror = (e) => console.log('[SCO-CHAT] WS error', e?.message || e);

        ws.onclose = () => {
          if (wsRef.current !== myWs) return;
          setWsReady(false);
          wsRef.current = null;
          if (!closed) {
            const d = Math.min(backoffRef.current, 8000);
            setTimeout(() => {
              backoffRef.current = Math.min(backoffRef.current * 2, 8000);
              if (!closed) {
                // re-run by changing a tiny state or just let the effect re-fire on a parent cue
                // simplest here: call fetchWsToken() chain again
                (async () => {
                  try { await fetchWsToken(); }
                  catch (err) { console.debug('[SCO-CHAT] token refresh failed', err); }
                })();
              }
            }, d);
          }
        };
      } catch (e) {
        connectingRef.current = false;
        console.log('[SCO-CHAT] WS bootstrap failed; HTTP-only for now', e.message);
      }
    })();

    return () => {
      closed = true;
      try { wsRef.current?.close(); }
      catch (e) { console.debug('[SCO-CHAT] ws close ignored', e); }
      wsRef.current = null;
      connectingRef.current = false;
    };
    // include taskId if you want auto re-subscribe on room change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const notify = useContext(NotificationContext);

  // Fetch messages once on mount/task change, then poll only if WebSocket isn't ready
  useEffect(() => {
    fetchChatMessages();
    if (wsReady) {
      console.log('[SCO-CHAT] WebSocket ready - no polling needed');
      return;
    }
    console.log('[SCO-CHAT] WebSocket not ready - starting polling as fallback');
    const intervalId = setInterval(fetchChatMessages, 10000);
    return () => clearInterval(intervalId);
  }, [taskId, wsReady]);

  useEffect(() => {
    customerEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [customerMessages]);

  useEffect(() => {
    mechanicEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mechanicMessages]);

  // Also, add this useEffect to scroll on initial load:
  useEffect(() => {
    // Small delay to ensure content is rendered before scrolling
    setTimeout(() => {
      customerEndRef.current?.scrollIntoView({ behavior: "auto" });
      mechanicEndRef.current?.scrollIntoView({ behavior: "auto" });
    }, 100);
  }, []);

  //   useEffect(() => {
  //     customerEndRef.current?.scrollIntoView({ behavior: "smooth" });
  //   }, [customerMessages]);

  //   useEffect(() => {
  //     mechanicEndRef.current?.scrollIntoView({ behavior: "smooth" });
  //   }, [mechanicMessages]);

  // Removed createNotification function - notifications are now handled by backend
  // (WebSocket server, sendMessage.php, postMedia.php)

  // Parse datetime string from backend as UTC (backend stores all timestamps in UTC)
  const parseUTCDateTime = (dateString) => {
    if (!dateString) return null;
    // Backend returns format: "YYYY-MM-DD HH:MM:SS"
    // Replace space with T and add Z to indicate UTC
    const isoString = dateString.replace(' ', 'T') + (dateString.includes('Z') ? '' : 'Z');
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Function to enable editing
  const handleEnableEdit = (message, setMessages) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.chat_id === message.chat_id ? { ...msg, isEditing: true, editedMessage: msg.message } : msg
      )
    );
  };

  // Function to handle edit text changes
  const handleEditMessageChange = (e, message, setMessages) => {
    const newText = e.target.value;
    setMessages((prevMessages) =>
      prevMessages.map((msg) => (msg.chat_id === message.chat_id ? { ...msg, editedMessage: newText } : msg))
    );
  };

  // Function to submit edited message
  const handleEditMessageSubmit = (message, setMessages) => {
    if (message.editedMessage.trim() === "") {
      notify("Message cannot be empty.","error");
      return;
    }

    fetch(envVariable.serverId + "/SCO/editMessage.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        chat_id: message.chat_id,
        new_message: message.editedMessage,
        user_id: User_Id,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.type === "success") {
          // Update the message locally
          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              msg.chat_id === message.chat_id
                ? {
                    ...msg,
                    message: message.editedMessage,
                    isEditing: false,
                    edited: 1,
                  }
                : msg
            )
          );

          // Notify other clients of the specific update via WebSocket
          if (wsRef.current && wsReady) {
            console.log('[SCO-CHAT] WS → message:updated', { chat_id: message.chat_id, new_message: message.editedMessage });
            try {
              wsRef.current.send(JSON.stringify({
                type: 'message:updated',
                task_id: Number(taskId),
                chat_id: message.chat_id,
                new_message: message.editedMessage,
              }));
            } catch (e) {
              console.debug('[SCO-CHAT] message:updated failed', e);
            }
          }
        } else {
          notify(data.message,"error");
          // Revert editing state
          setMessages((prevMessages) =>
            prevMessages.map((msg) => (msg.chat_id === message.chat_id ? { ...msg, isEditing: false } : msg))
          );
        }
      })
      .catch((error) => {
        console.error("Error editing message:", error);
        notify("An error occurred while editing the message.","error");
        // Revert editing state
        setMessages((prevMessages) =>
          prevMessages.map((msg) => (msg.chat_id === message.chat_id ? { ...msg, isEditing: false } : msg))
        );
      });
  };

  // Function to delete a message (soft delete)
  const handleDeleteMessage = (chatId) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      fetch(`${envVariable.serverId}/SCO/deleteMessage.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chat_id: chatId,
          user_id: User_Id,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Response from server:", data);
          if (data.type === "success") {
            // Mark the message as deleted in local state by setting message to special marker
            setCustomerMessages((prevMessages) =>
              prevMessages.map((msg) => (msg.chat_id === chatId ? { ...msg, message: '__DELETED__' } : msg))
            );
            setMechanicMessages((prevMessages) =>
              prevMessages.map((msg) => (msg.chat_id === chatId ? { ...msg, message: '__DELETED__' } : msg))
            );

            // Notify other clients of the specific deletion via WebSocket
            if (wsRef.current && wsReady) {
              console.log('[SCO-CHAT] WS → message:deleted', { chat_id: chatId });
              try {
                wsRef.current.send(JSON.stringify({
                  type: 'message:deleted',
                  task_id: Number(taskId),
                  chat_id: chatId,
                }));
              } catch (e) {
                console.debug('[SCO-CHAT] message:deleted failed', e);
              }
            }
          } else {
            console.error(data.message);
            notify(data.message,"error");
          }
        })
        .catch((error) => {
          console.error("Error deleting message:", error);
          notify("An error occurred while deleting the message.","error");
        });
    }
  };

  // Function to fetch message edit history
  const fetchMessageHistory = async (chatId) => {
    try {
      const response = await fetch(`${envVariable.serverId}/SCO/getMessageHistory.php?chat_id=${chatId}`);
      const data = await response.json();

      console.log("Message history response:", data);

      if (data.type === "success") {
        console.log("History data:", data.history);
        setMessageHistory(data.history || []);
        setShowHistoryModal(true);
      } else {
        notify("Failed to load message history.", "error");
      }
    } catch (error) {
      console.error("Error fetching message history:", error);
      notify("An error occurred while loading message history.", "error");
    }
  };

  // Function to send a message
  const handleSendMessage = async (recipient, message, setMessageFn, setMessagesFn) => {
    const recipientId = recipient === "customer" ? customerId : mechanicId;
    if (message.trim() === "") return;

    // --- WS path (preferred) ---
    if (wsRef.current && wsReady) {
      console.log('[SCO-CHAT] WS path → message:new', {
        task_id: Number(taskId),
        recipient_id: Number(recipientId),
        preview: message.slice(0, 40),
      });

      try {
        wsRef.current.send(JSON.stringify({
          type: 'message:new',
          task_id: Number(taskId),
          recipient_id: Number(recipientId),
          message,
        }));

        // Optimistic input clear; row will arrive via WS broadcast
        setMessageFn("");

        // Safety resync if any broadcast is missed
        setTimeout(() => {
          console.log('[SCO-CHAT] safety resync (if broadcast missed)');
          // optional: check state and call fetchChatMessages() if not found
        }, 1500);

        // Notification is automatically created by WebSocket server
        // (removed duplicate createNotification call)
        return;
      } catch (e) {
        console.log('[SCO-CHAT] WS send failed, fallback HTTP:', e.message);
      }
    } else {
      console.log('[SCO-CHAT] WS not ready, HTTP fallback');
    }

    // --- HTTP fallback (existing behavior) ---
    try {
      const formData = new FormData();
      formData.append("recipient_id", recipientId);
      formData.append("message", message);
      formData.append("task_id", taskId);
      formData.append("user_id", User_Id);
      // Backend generates its own UTC timestamp, so we don't send created_at

      const response = await fetch(envVariable.serverId + "/SCO/postMessage.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.type === "success" || data.success) {
        // Append the new message locally for instant UI update
        // Use current time as placeholder until real data arrives from server
        const newMessage = {
          sender_id: User_Id,
          recipient_id: recipientId,
          message,
          created_at: new Date().toISOString().replace('T', ' ').slice(0, 19),
          isEditing: false,
          editedMessage: "",
          chat_id: data.chat_id,
        };
        setMessagesFn((prev) => [...prev, newMessage]);
        setMessageFn("");
        // Notification handled by backend (sendMessage.php or WebSocket)
        // (removed duplicate createNotification call)

        // Tell live subscribers to resync
        if (wsRef.current && wsReady) {
          try {
            console.log('[SCO-CHAT] WS → notify:resync (http text)');
            wsRef.current.send(JSON.stringify({ type: 'notify:resync', task_id: Number(taskId) }));
          } catch(e) {console.debug('[SCO-CHAT] notify:resync failed', e);}
        }
      } else {
        console.error("Failed to send message:", data.message || data.error);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Function to fetch chat messages
const fetchChatMessages = useCallback(async () => {
  try {
    const response = await fetch(`${envVariable.serverId}/SCO/getChatMessages.php?TaskId=${taskId}&SC_Id=${SC_Id}`);
    if (!response.ok) throw new Error("Network response was not ok");
    const data = await response.json();

    const messages = data.messages || [];

    //Partho debugging Logs
    console.log("First message from API:", messages[0]?.message);
    console.log("First message length:", messages[0]?.message?.length);

    // Categorize messages based on sender and recipient IDs
    const customerMessagesData = messages
      .filter((msg) => msg.sender_id === customerId || msg.recepient_id === customerId)
      .map((msg) => ({
        ...msg,
        recipient_id: msg.recepient_id,
        isEditing: false,
        editedMessage: msg.message,
        mediaType: msg.message_type,
      }));
    const mechanicMessagesData = messages
      .filter((msg) => msg.sender_id === mechanicId || msg.recepient_id === mechanicId)
      .map((msg) => ({
        ...msg,
        recipient_id: msg.recepient_id,
        isEditing: false,
        editedMessage: msg.message,
        mediaType: msg.message_type,
      }));

    setCustomerMessages(customerMessagesData);
    setMechanicMessages(mechanicMessagesData);
  } catch (error) {
    console.error("Error fetching messages:", error);
  }
}, [taskId, SC_Id, customerId, mechanicId]); // Add all dependencies

  // Function to transfer media between chats
  const handleTransferMedia = async (sourceRecipient, targetRecipient, mediaPath, messageType, setMessagesFn) => {
    // Debugging: Log the data being sent
    console.log("handleTransferMedia called with:", {
      sourceRecipient,
      targetRecipient,
      mediaPath,
      taskId,
      userId: User_Id,
      messageType,
    });

    try {
      const response = await fetch(`${envVariable.serverId}/SCO/transferMedia.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sourceRecipient,
          targetRecipient,
          mediaPath,
          taskId,
          userId: User_Id,
          messageType: messageType || "image", // Default to 'image'
        }),
      });

      const data = await response.json();

      if (data.type === "success") {
        // Update the chat messages locally
        const newMessage = {
          sender_id: User_Id,
          recipient_id: targetRecipient,
          message: "",
          media: data.mediaPath,
          mediaType: data.mediaType,
          created_at: new Date().toISOString().replace('T', ' ').slice(0, 19),
          isSent: true,
          isEditing: false,
          editedMessage: "",
          chat_id: data.chat_id,
        };

        setMessagesFn((prev) => [...prev, newMessage]);
      } else {
        console.error("Error transferring media:", data.message);
      }
    } catch (error) {
      console.error("Error transferring media:", error);
    }
  };
  // Function to handle file uploads (images/videos)
  const handleFileUpload = async (type, recipientId, role, file) => {
    if (!file) {
      console.error("No file selected for upload.");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      // Backend generates its own UTC timestamp
      formData.append(type === "image" ? "imageDat" : "videoDat", file);
      formData.append("user_id", User_Id);
      formData.append("recipient_id", recipientId);
      formData.append("message_type", type);
      formData.append("task_id", taskId);

      const response = await fetch(`${envVariable.serverId}/SCO/postMedia.php`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      setUploading(false);

      if (data.type === "success") {
        console.log("File uploaded successfully.");

        const newMessage = {
          sender_id: User_Id,
          recipient_id: recipientId,
          message: "",
          media: data.url,
          mediaType: type,
          message_type: type,
          created_at: new Date().toISOString().replace('T', ' ').slice(0, 19),
          isSent: true,
          isEditing: false,
          editedMessage: "",
          chat_id: data.chat_id,
        };

        if (role === "mechanic") {
          setMechanicMessages((prev) => [...prev, newMessage]);
        } else {
          setCustomerMessages((prev) => [...prev, newMessage]);
        }

        // Notification handled by backend (postMedia.php)
        // (removed duplicate createNotification call)
        // Nudge channel to refresh media via HTTP, then broadcast
        if (wsRef.current && wsReady) {
          console.log('[SCO-CHAT] WS → notify:resync (media)');
          try {
            wsRef.current.send(JSON.stringify({
              type: 'notify:resync',
              task_id: Number(taskId),
            }));
          } catch (e) {
            console.log('[SCO-CHAT] notify:resync failed', e.message);
          }
        }
      } else {
        console.error("Failed to upload file:", data.message);
      }
    } catch (error) {
      setUploading(false);
      console.error("Error uploading file:", error);
    }
  };

  return (
    <Box>
      <Header title={`Chat - Task #${taskId}`} />
      <Grid container spacing={2} sx={{ p: 3 }}>
        {/* Customer Chat */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Messages with Customer {customerName}</Typography>
            <Box
              sx={{
                height: "400px",
                overflowY: "auto",
                mb: 2,
              }}
            >
              {customerMessages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: message.sender_id === User_Id ? "flex-end" : "flex-start",
                    mb: 1,
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      paddingTop: 3,
                      backgroundColor:
                        message.sender_id === User_Id ? (message.isEditing ? "grey.500" : "primary.main") : "grey.600",
                        color: message.sender_id === User_Id ? "white" : "black",
                      maxWidth: "80%",
                      minWidth: "120px",
                      position: "relative",
                    }}
                  >
                    {message.isEditing ? (
                      <TextField
                        fullWidth
                        value={message.editedMessage}
                        onChange={(e) => handleEditMessageChange(e, message, setCustomerMessages)}
                        onBlur={() => handleEditMessageSubmit(message, setCustomerMessages)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleEditMessageSubmit(message, setCustomerMessages);
                          }
                        }}
                        autoFocus
                        multiline
                      />
                    ) : (
                      <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                        {message.message === '__DELETED__' ? (
                          <span style={{ fontStyle: "italic", color: "#888" }}>[deleted]</span>
                        ) : (
                          <>
                            {message.message}
                            {message.edited === 1 && (
                              <>
                                <Typography variant="caption" sx={{ fontStyle: "italic", ml: 1 }}>
                                  (edited)
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => fetchMessageHistory(message.chat_id)}
                                  sx={{ ml: 0.5, padding: 0 }}
                                  aria-label="View message history"
                                >
                                  <History fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </>
                        )}
                      </Typography>
                    )}
                    {/* Media display logic - hide if deleted */}
                    {message.media && message.message !== '__DELETED__' && (
                      <Box sx={{ mt: 1 }}>
                        {message.mediaType === "image" || message.mediaType === undefined ? (
                          <img
                            src={message.media}
                            alt="media"
                            onClick={() => setLightboxImage(message.media)}
                            style={{
                              maxWidth: "100%",
                              cursor: "pointer",
                            }}
                          />
                        ) : (
                          <video
                            controls
                            src={message.media}
                            style={{
                              maxWidth: "100%",
                              cursor: "pointer",
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {message.sender_id === User_Id && !message.isEditing && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 1, gap: 0.5 }}>
                        {!message.media && message.message !== '__DELETED__' && (
                          <IconButton
                            size="small"
                            sx={{
                              color: "white",
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                              padding: "2px",
                              minWidth: "16px",
                              height: "16px",
                            }}
                            onClick={() => handleEnableEdit(message, setCustomerMessages)}
                          >
                            <Edit sx={{ fontSize: 12 }}/>
                          </IconButton>
                        )}
                        {message.media && message.message !== '__DELETED__' && (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              color: "white",
                              borderColor: "white",
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              minWidth: "auto",
                            }}
                            onClick={() =>
                              handleTransferMedia(
                                message.recipient_id,
                                mechanicId,
                                message.media,
                                message.mediaType || "image",
                                setMechanicMessages
                              )
                            }
                          >
                            Transfer
                          </Button>
                        )}
                        {message.message !== '__DELETED__' && (
                          <IconButton
                            size="small"
                            sx={{
                              color: "white",
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                              padding: "2px",
                              minWidth: "16px",
                              height: "16px",
                            }}
                            onClick={() => handleDeleteMessage(message.chat_id)}
                          >
                            <Close sx={{ fontSize: 12 }} />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              ))}
              <div ref={customerEndRef} />
            </Box>

            {/* Input for customer messages */}
            <Grid container spacing={1}>
              <Grid item xs={9}>
                <TextField
                  fullWidth
                  multiline
                  label="Type your message to the customer..."
                  value={newCustomerMessage}
                  onChange={(e) => setNewCustomerMessage(e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    handleSendMessage("customer", newCustomerMessage, setNewCustomerMessage, setCustomerMessages)
                  }
                  disabled={newCustomerMessage.trim() === ""}
                  fullWidth
                >
                  Send
                </Button>
              </Grid>
            </Grid>

            {/* File Upload */}
            <Grid container spacing={1} mt={1}>
              <Grid item>
                <IconButton color="primary" component="label">
                  <Image />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload("image", customerId, "customer", file);
                      }
                    }}
                  />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton color="primary" component="label">
                  <Videocam />
                  <input
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload("video", customerId, "customer", file);
                      }
                    }}
                  />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Mechanic Chat */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Messages with Employee {mechanicName}:</Typography>
            <Box
              sx={{
                height: "400px",
                overflowY: "auto",
                mb: 2,
              }}
            >
              {mechanicMessages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: message.sender_id === User_Id ? "flex-end" : "flex-start",
                    mb: 1,
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      paddingTop: 3,
                      backgroundColor:
                        message.sender_id === User_Id ? (message.isEditing ? "grey.500" : "primary.main") : "grey.600",
                        color: message.sender_id === User_Id ? "white" : "black",
                      maxWidth: "80%",
                      minWidth: "120px",
                      position: "relative",
                    }}
                  >
                    {message.isEditing ? (
                      <TextField
                        fullWidth
                        value={message.editedMessage}
                        onChange={(e) => handleEditMessageChange(e, message, setMechanicMessages)}
                        onBlur={() => handleEditMessageSubmit(message, setMechanicMessages)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleEditMessageSubmit(message, setMechanicMessages);
                          }
                        }}
                        autoFocus
                        multiline
                      />
                    ) : (
                      <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                        {message.message === '__DELETED__' ? (
                          <span style={{ fontStyle: "italic", color: "#888" }}>[deleted]</span>
                        ) : (
                          <>
                            {message.message}
                            {message.edited === 1 && (
                              <>
                                <Typography variant="caption" sx={{ fontStyle: "italic", ml: 1 }}>
                                  (edited)
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => fetchMessageHistory(message.chat_id)}
                                  sx={{ ml: 0.5, padding: 0 }}
                                  aria-label="View message history"
                                >
                                  <History fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </>
                        )}
                      </Typography>
                    )}
                    {/* Media display logic - hide if deleted */}
                    {message.media && message.message !== '__DELETED__' && (
                      <Box sx={{ mt: 1 }}>
                        {message.mediaType === "image" || message.mediaType === undefined ? (
                          <img
                            src={message.media}
                            alt="media"
                            onClick={() => setLightboxImage(message.media)}
                            style={{
                              maxWidth: "100%",
                              cursor: "pointer",
                            }}
                          />
                        ) : (
                          <video
                            controls
                            src={message.media}
                            style={{
                              maxWidth: "100%",
                              cursor: "pointer",
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {message.sender_id === User_Id && !message.isEditing && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 1, gap: 0.5 }}>
                        {!message.media && message.message !== '__DELETED__' && (
                          <IconButton
                            size="small"
                            sx={{
                              color: "white",
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                              padding: "2px",
                              minWidth: "16px",
                              height: "16px",
                            }}
                            onClick={() => handleEnableEdit(message, setMechanicMessages)}
                          >
                            <Edit sx={{ fontSize: 12 }} />
                          </IconButton>
                        )}
                        {message.media && message.message !== '__DELETED__' && (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              color: "white",
                              borderColor: "white",
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              minWidth: "auto",
                            }}
                            onClick={() =>
                              handleTransferMedia(
                                message.recipient_id,
                                customerId,
                                message.media,
                                message.mediaType || "image",
                                setCustomerMessages
                              )
                            }
                          >
                            Transfer
                          </Button>
                        )}
                        {message.message !== '__DELETED__' && (
                          <IconButton
                            size="small"
                            sx={{
                              color: "white",
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
                              padding: "2px",
                              minWidth: "16px",
                              height: "16px",
                            }}
                            onClick={() => handleDeleteMessage(message.chat_id)}
                          >
                            <Close sx={{ fontSize: 12 }} />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              ))}
              <div ref={mechanicEndRef} />
            </Box>

            {/* Input for mechanic messages */}
            <Grid container spacing={1}>
              <Grid item xs={9}>
                <TextField
                  fullWidth
                  multiline
                  label="Type your message to the employee..."
                  value={newMechanicMessage}
                  onChange={(e) => setNewMechanicMessage(e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    handleSendMessage("mechanic", newMechanicMessage, setNewMechanicMessage, setMechanicMessages)
                  }
                  disabled={newMechanicMessage.trim() === ""}
                  fullWidth
                >
                  Send
                </Button>
              </Grid>
            </Grid>

            {/* File Upload */}
            <Grid container spacing={1} mt={1}>
              <Grid item>
                <IconButton color="primary" component="label">
                  <Image />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload("image", mechanicId, "mechanic", file);
                      }
                    }}
                  />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton color="primary" component="label">
                  <Videocam />
                  <input
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload("video", mechanicId, "mechanic", file);
                      }
                    }}
                  />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {/* Message History Modal */}
      <Dialog
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Message Edit History</DialogTitle>
        <DialogContent>
          {messageHistory.length > 0 ? (
            <List>
              {messageHistory.map((entry, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={entry.message}
                    secondary={`Edited at: ${parseUTCDateTime(entry.edited_at)?.toLocaleString() || 'Unknown'}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>No edit history available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Image Lightbox Modal */}
<Modal
  open={lightboxImage !== null}
  onClose={() => setLightboxImage(null)}
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  <Box
    onClick={() => setLightboxImage(null)}
    sx={{
      outline: "none",
      maxWidth: "90vw",
      maxHeight: "90vh",
    }}
  >
    {lightboxImage && (
      <img
        src={lightboxImage}
        alt="Full size"
        style={{
          maxWidth: "100%",
          maxHeight: "90vh",
          objectFit: "contain",
        }}
      />
    )}
  </Box>
</Modal>
      <Footer />
    </Box>
  );
};

export default Chat;
